import { keycloakInstance } from "@king-ict/eupisi-ui/auth";
import { PagedResult, CodeBook } from "@king-ict/eupisi-ui/models";
import { api } from "@king-ict/eupisi-ui/utils";
import axios from "axios";

import {
  ProgramRequestListItem, ProgramRequest,
  CreateProgramRequestRequest, EditProgramRequestRequest,
  ProgramRequestEnvelopeListItem,
  OtherLegalEntityLocation,
  ExecutionResolutionDocument,
  ProgramRequestDetail,
  TakeIntoProcessingRequest,
  GetHistoricProgramRequestDetailRequest,
  ProgramRequestStatusHistory,
  AssignAdministratorRequest,
  EnvelopeClassificationRequest,
  OtherLocationRequest,
  ManualStatusChangeRequest,
  ManualStatusTransitionOption
} from "../../models/ProgramRequest";
import { CreateProgramRequestsResolutionDocumentsRequest } from "../../models/ProgramRequest/CreateProgramRequestsResolutionDocumentsRequest";
import { CreateResolutionDocumentRequest } from "../../models/ProgramRequest/CreateResolutionDocumentRequest";
import { ProgramRequestTeacher } from "../../models/ProgramRequest/ProgramRequestTeacher";
import { CreateProgramRequestStatus } from "./CreateProgramRequestStatus";
import { ExportProgramRequestAgencyRequest } from "./ExportProgramRequestAgencyRequest";
import { ExportProgramRequestInstitutionRequest } from "./ExportProgramRequestInstitutionRequest";
import { ExportProgramRequestMinistryRequest } from "./ExportProgramRequestMinistryRequest";
import { SearchNISOO2ProgramRequestListRequest } from "./SearchNISOO2ProgramRequestListRequest";
import { SearchProgramRequestsAgencyRequest } from "./SearchProgramRequestsAgencyRequest";
import { SearchProgramRequestsInstitutionRequest } from "./SearchProgramRequestsInstitutionRequest";
import { SearchProgramRequestsMinistryRequest } from "./SearchProgramRequestsMinistryRequest";


const programRequestsUrlPrefix: string = "program-requests";

type CreateProgramRequestResponse = number | { id: number };

export const createProgramRequest = async (request: CreateProgramRequestRequest): Promise<number> => {
  const result = (await api.post(
    programRequestsUrlPrefix,
    request
  )) as CreateProgramRequestResponse;

  return typeof result === "number" ? result : result.id;
};

export const editProgramRequest = async (programRequestId: number, request: EditProgramRequestRequest) => {
  return api.put(
    `${programRequestsUrlPrefix}/${programRequestId}`,
    request
  ) as Promise<void>;
};

export const createProgramRequestResolution = async (programRequestId: number, request: CreateResolutionDocumentRequest) => {
  return api.post(
    `${programRequestsUrlPrefix}/resolution/${programRequestId}`,
    request
  ) as Promise<void>;
};

export const createProgramRequestResolutions = async (request: CreateProgramRequestsResolutionDocumentsRequest) => {
  return api.post(
    `${programRequestsUrlPrefix}/resolutions`,
    request
  ) as Promise<void>;

};

export const saveOtherLegalEntityLocation = async (programRequestId: number, request: OtherLegalEntityLocation) => {
  return api.post(
    `${programRequestsUrlPrefix}/other-legal-entity-location/${programRequestId}`,
    request
  ) as Promise<OtherLegalEntityLocation>;
};

export const saveForeignLanguage = async (programRequestId: number, request: ProgramRequestTeacher[]) => {
  return api.post(
    `${programRequestsUrlPrefix}/foreign-language/${programRequestId}`,
    request
  ) as Promise<ProgramRequestTeacher[]>;
};

export const saveTeachers = async (programRequestId: number, request: ProgramRequestTeacher[]) => {
  return api.post(
    `${programRequestsUrlPrefix}/teachers/${programRequestId}`,
    request
  ) as Promise<void>;
};

export const searchProgramRequestsInstitution = async (request: SearchProgramRequestsInstitutionRequest) => {
  var result = await (api.get(
    programRequestsUrlPrefix + "/search-institution",
    request
  ) as Promise<PagedResult<ProgramRequestListItem>>);
  setDatesProgramRequestListItem(result.results);
  return result;
};

export const exportProgramRequestInstitution = async (request: ExportProgramRequestInstitutionRequest) => {
  var result = await (api.download(
    programRequestsUrlPrefix + "/export-institution",
    "get",
    request
  ) as Promise<unknown>);
  return result;
};

export const searchProgramRequestsMinistry = async (request: SearchProgramRequestsMinistryRequest) => {
  var result = await (api.get(
    programRequestsUrlPrefix + "/search-ministry",
    request
  ) as Promise<PagedResult<ProgramRequestListItem>>);
  setDatesProgramRequestListItem(result.results);
  return result;
};

export const exportProgramRequestMinistry = async (request: ExportProgramRequestMinistryRequest) => {
  var result = await (api.download(
    programRequestsUrlPrefix + "/export-ministry",
    "get",
    request
  ) as Promise<unknown>);
  return result;
};

export const searchProgramRequestsAgency = async (request: SearchProgramRequestsAgencyRequest) => {
  var result = await (api.get(
    programRequestsUrlPrefix + "/search-agency",
    request
  ) as Promise<PagedResult<ProgramRequestListItem>>);
  setDatesProgramRequestListItem(result.results);
  return result;
};

export const exportProgramRequestAgency = async (request: ExportProgramRequestAgencyRequest) => {
  var result = await (api.download(
    programRequestsUrlPrefix + "/export-agency",
    "get",
    request
  ) as Promise<unknown>);
  return result;
};

export const downloadProgramRequestPdf = async (requestId: number) => {
  var result = await (api.download(
    `${programRequestsUrlPrefix}/download-pdf/${requestId}`,
    "get",
  ) as Promise<unknown>);
  return result;
};

export const getProgramRequestDetail = async (requestId: number) => {
  var result = await (api.get(
    `${programRequestsUrlPrefix}/detail/${requestId}`,
  ) as Promise<ProgramRequest>);

  setDatesProgramRequestDetailResponse(result);
  return result;
};

export const getHistoricProgramRequestDetail = async (request: GetHistoricProgramRequestDetailRequest) => {
  var result = await (api.get(
    `${programRequestsUrlPrefix}/historic-detail`, request
  ) as Promise<ProgramRequest>);
  return result;
};

export const getProgramRequestStatusHistoryById = async (historyId: number) => {
  var result = await (api.get(
    `${programRequestsUrlPrefix}/history-data/${historyId}`,
  ) as Promise<ProgramRequestStatusHistory>);
  return result;
};

export const setProgramRequestStatus = async (request: CreateProgramRequestStatus) => {
  return api.put(
    `${programRequestsUrlPrefix}/create-request-status`,
    request
  ) as Promise<string>;
};


// Bypasses the global ResponseHandler interceptors
const silentApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

/**
 * Statuses the request may be manually moved to from its current status (Helpdesk role).
 * The BE answers 403 when the change is not allowed
 */
export const getAllowedStatusTransitions = async (
  programRequestId: number
): Promise<ManualStatusTransitionOption[]> => {
  try {
    const response = await silentApi.get<ManualStatusTransitionOption[]>(
      `${programRequestsUrlPrefix}/${programRequestId}/allowed-status-transitions`,
      {
        headers: {
          Authorization: keycloakInstance ? `Bearer ${keycloakInstance.token}` : undefined,
        },
      }
    );
    return response.data ?? [];
  } catch (error: any) {
    if (error?.response?.status !== 403) {
      // eslint-disable-next-line no-console
      console.warn("Failed to fetch the allowed status transitions.", error);
    }
    return [];
  }
};

export const manualChangeProgramRequestStatus = async (request: ManualStatusChangeRequest) => {
  return api.put(
    `${programRequestsUrlPrefix}/manual-status-change`,
    request
  ) as Promise<void>;
};

export const createSingleProgramRequestGroup = async (request: CreateProgramRequestStatus) => {
  return api.put(
    `${programRequestsUrlPrefix}/create-single-request-group`,
    request
  ) as Promise<string>;
};

export const undoProgramRequestChanges = async (programRequestId: number) => {
  return api.put(
    `${programRequestsUrlPrefix}/undo-request-changes/${programRequestId}`,
  ) as Promise<void>;
};

export const createProgramRequestCorrection = async (programRequestId: number, request: EditProgramRequestRequest) => {
  return api.post(
    `${programRequestsUrlPrefix}/correction/${programRequestId}`,
    request
  ) as Promise<void>
};

export const getUnlockRequestCount = async (institutionId: number) => {
  return api.get(
    `${programRequestsUrlPrefix}/unlock-request-count/${institutionId}`
  ) as Promise<number>;
};

const setDatesProgramRequestDetailResponse = (programRequest: ProgramRequest) => {
  programRequest.requestDate = new Date(programRequest.requestDate);
}

const setDatesProgramRequestListItem = (items: ProgramRequestListItem[]) => {
  for (let item of items) {
    item.programRequestDate = new Date(item.programRequestDate);
  }
}

export const searchClassificationMarksCodebook = async (search: string) => {
  let request: any = { search: search }
  let url = `${programRequestsUrlPrefix}/search-classification-marks?search=${search}`;
  return api.get(url, request) as Promise<CodeBook[]>;
}

export const getProgramRequestListAsync = async (filter: SearchNISOO2ProgramRequestListRequest) => {
  return api.get(`${programRequestsUrlPrefix}/list`, filter) as Promise<PagedResult<ProgramRequestEnvelopeListItem>>
}

export const getEnvelopeData = async (envelopeId: number) => {
  return api.get(`${programRequestsUrlPrefix}/program-request-envelope-data/${envelopeId}`) as Promise<ProgramRequestEnvelopeListItem>
};

export const getProgramRequestTeacher = async (requestId: number) => {
  var result = await (api.get(
    `teachers/requestid/${requestId}`, //${programRequestsUrlPrefix}/
  ) as Promise<ProgramRequestTeacher[]>);

  // setDatesProgramRequestDetailResponse(result);
  return result;
};

export const createConsent = async (requestId: number, consent: ExecutionResolutionDocument) => {
  return api.post(
    `${programRequestsUrlPrefix}/consent/${requestId}`,
    consent
  ) as Promise<ExecutionResolutionDocument>;
};

export const createHarmonization = async (requestId: number, details: ProgramRequestDetail) => {
  return api.post(
    `${programRequestsUrlPrefix}/harmonization/${requestId}`,
    details
  ) as Promise<ProgramRequestDetail>;
};

export const checkTeacherOib = async (oib: string, firstname: string, lastname: string) => {
  var result = await (api.get(
    `teachers/oib/${oib}/${firstname}/${lastname}`
  ) as Promise<boolean>);

  // setDatesProgramRequestDetailResponse(result);
  return result;
};

export const takeIntoProcessing = async (takeIntoProcessingRequest: TakeIntoProcessingRequest) => {
  return api.put(
    `${programRequestsUrlPrefix}/take-into-processing`,
    takeIntoProcessingRequest
  ) as Promise<void>;
};

export const takeOverProcessing = async (envelopeId: number) => {
  return api.put(
    `${programRequestsUrlPrefix}/take-over-processing/${envelopeId}`
  ) as Promise<void>;
};

export const assignAdministrator = async (assignAdministrator: AssignAdministratorRequest) => {
  return api.put(
    `${programRequestsUrlPrefix}/assign-administrator`,
    assignAdministrator
  ) as Promise<void>;
};

export const reassignAdministrator = async (assignAdministrator: AssignAdministratorRequest) => {
  return api.put(
    `${programRequestsUrlPrefix}/reassign-administrator`,
    assignAdministrator
  ) as Promise<void>;
};

export const editEnvelope = async (editEnvelope: EnvelopeClassificationRequest) => {
  return api.put(
    `${programRequestsUrlPrefix}/program-request-envelope`,
    editEnvelope
  ) as Promise<void>;
};

export const saveOtherLocations = async (otherLocationsRequest: OtherLocationRequest) => {
  return api.post(
    `${programRequestsUrlPrefix}/other-locations`,
    otherLocationsRequest) as Promise<void>;
};

export const submitPausedProgramRequest = async (programRequestId: number) => {
  return api.put(
    `${programRequestsUrlPrefix}/submit-paused-request/${programRequestId}`
  ) as Promise<void>;
};


